import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ReportFile } from "../../../types/Rows";
import { deleteFile, GetReportFile, updateReportFileLabel, UploadFile } from "../../../api/reportsApi";
import { Button } from "../../../components/ui/button";
import { failurToast, successToast } from "../../../helpers/successToast";

function FileLabelInput({
  fileId,
  reportId,
  initialLabel,
  queryKey,
}: {
  fileId: string;
  reportId: string;
  initialLabel: string;
  queryKey: string[];
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(initialLabel);
  const [editing, setEditing] = useState(false);

  const { mutate: saveLabel } = useMutation(
    (label: string) => updateReportFileLabel(reportId, fileId, label),
    { onSuccess: () => queryClient.resetQueries(queryKey) }
  );

  const commit = () => {
    setEditing(false);
    if (value !== initialLabel) saveLabel(value);
  };

  if (editing) {
    return (
      <input
        autoFocus
        className="w-full text-xs border border-[var(--sc-border)] rounded px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[var(--sc-amber)]"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") { setValue(initialLabel); setEditing(false); }
        }}
        placeholder="Dodaj opis..."
        maxLength={256}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="w-full text-left text-xs text-[var(--sc-muted)] hover:text-[var(--sc-text)] truncate"
      title={value || "Kliknij, aby dodać opis"}
    >
      {value || <span className="italic">Dodaj opis…</span>}
    </button>
  );
}

export function ReportFiles({
  files,
  reportId,
}: {
  files: ReportFile[];
  reportId: string;
}) {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryKey = [reportId, "Files"];

  const { mutate: upload } = useMutation(
    async (file: File) => {
      const formData = new FormData();
      formData.append("file", file);
      return UploadFile(reportId, formData, file.name);
    },
    {
      onSuccess: () => {
        successToast();
        queryClient.resetQueries(queryKey);
      },
      onError: () => failurToast("Nie udało się załączyć pliku"),
    }
  );

  const { mutate: remove } = useMutation(
    async (fileId: string) => deleteFile(reportId, fileId),
    {
      onSuccess: () => {
        successToast();
        queryClient.resetQueries(queryKey);
      },
      onError: () => failurToast("Nie udało się usunąć pliku"),
    }
  );

  const handleDownload = async (file: ReportFile) => {
    const bytes = await GetReportFile(file.id, file.id);
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const inputCls = "block text-xs font-medium text-[var(--sc-muted)] uppercase tracking-[0.05em]";

  return (
    <div className="space-y-2">
      <p className={inputCls}>Załączniki:</p>
      {files.length === 0 && (
        <p className="text-sm text-[var(--sc-muted)]">Brak załączników</p>
      )}
      {files.map((f) => (
        <div
          key={f.id}
          className="flex flex-col gap-1 px-3 py-2 rounded-lg border border-[#EBEBEA] bg-white"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-[var(--sc-text)] truncate flex-1 min-w-0">{f.name}</span>
            <Button variant="secondary" size="sm" onClick={() => handleDownload(f)}>
              Pobierz
            </Button>
            <Button variant="ghost" size="sm" onClick={() => remove(f.id)}>
              Usuń
            </Button>
          </div>
          <FileLabelInput
            fileId={f.id}
            reportId={reportId}
            initialLabel={f.label ?? ""}
            queryKey={queryKey}
          />
        </div>
      ))}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) upload(file);
          e.target.value = "";
        }}
      />
      <Button
        variant="secondary"
        size="sm"
        type="button"
        onClick={() => fileInputRef.current?.click()}
      >
        Dodaj załącznik
      </Button>
    </div>
  );
}
